<template>
  <!-- ALERTA GLOBAL -->
  <v-alert
    v-if="showAlert"
    :type="alertType"
    :class="['mx-auto','text-center','bg-' + (alertType === 'success' ? 'green-lighten-4' : 'red-lighten-4')]"
    style="position: fixed; top: 20px; left: 0; right: 0; z-index: 9999; max-width: 600px;"
    elevation="2"
    prominent
  >
    {{ alertMessage }}
  </v-alert>

  <v-container fluid>
    <v-card class="pa-4" elevation="2">
      <!-- CAPÇALERA -->
      <v-row class="align-center">
        <v-col class="headline mb-4">
          <h2>Campañas de rexpedición</h2>
          <div class="text-body-2 text-medium-emphasis mt-1">
            Lista de campañas RX pendientes de marcar como rexpedidas.
          </div>
        </v-col>

        <v-col cols="12" sm="6" class="text-right">
          <v-btn
            color="primary"
            class="mr-2"
            :loading="isSaving"
            :disabled="isLoading || isSaving || selectedRows.length === 0"
            @click="saveRexpedidas"
          >
            Guardar campañas rexpedidas
          </v-btn>
          <v-btn class="mr-2" :disabled="isLoading" @click="fetchCampaigns">
            Recargar
          </v-btn>
          <v-btn @click="$router.back()">Volver</v-btn>
        </v-col>
      </v-row>

      <!-- OVERLAY CÀRREGA -->
      <v-overlay :model-value="isLoading || isSaving" class="align-center justify-center" persistent>
        <v-progress-circular indeterminate size="48" />
      </v-overlay>

      <!-- TAULA CAMPANYES -->
      <v-data-table
        v-model:expanded="expanded"
        v-model="selectedRows"
        :items="campaigns"
        :headers="headers"
        :loading="isLoading"
        loading-text="Cargando campañas..."
        show-expand
        show-select
        item-value="id"
        class="elevation-1"
        return-object
        item-key="id"
        no-data-text="No hay campañas pendientes de rexpedición"
      >
        <!-- Format numèric -->
        <template #item.unitatsTotals="{ item }">
          {{ formatNumber(item.unitatsTotals) }}
        </template>

        <!-- Descripció curta -->
        <template #item.descripcio="{ item }">
          <span v-if="item.descripcio">
            {{ truncate(item.descripcio, 80) }}
          </span>
          <span v-else class="text-disabled">—</span>
        </template>

        <!-- Fila expandida: proveïdors / referències -->
        <template #expanded-row="{ columns, item }">
          <tr class="bg-grey-lighten-4">
            <td :colspan="columns.length">
              <v-card flat class="pa-3">
                <h3 class="text-subtitle-1 mb-2">
                  Detalle de campaña: {{ item.nom }}
                </h3>

                <div class="text-body-2 mb-3">
                  <div><strong>Cliente:</strong> {{ item.client || '—' }}</div>
                  <div>
                    <strong>Fechas:</strong>
                    {{ formatDateHuman(item.data_inici) }}
                    <span v-if="item.data_fi"> - {{ formatDateHuman(item.data_fi) }}</span>
                  </div>
                </div>

                <v-data-table
                  :items="item.linies || []"
                  :headers="lineHeaders"
                  density="compact"
                  class="elevation-0"
                  hide-default-footer
                  :items-per-page="-1"
                >
                <!-- :items-per-page="-1" es perque mostri totes les linies i no pagini-->
                  <template #item.quantitat="{ item: line }">
                    {{ formatNumber(line.quantitat) }}
                  </template>
                </v-data-table>
              </v-card>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import axios from 'axios'
import { useRouter } from 'vue-router'
import { useAlert } from '@/composables/useAlert'

const router = useRouter()
const { alertMessage, alertType, showAlert, showAlertMessage } = useAlert()

// Estat bàsic
const isLoading = ref(false)
const isSaving = ref(false)
const campaigns = ref([])          // files transformades
const expanded = ref([])           // files expandides
const selectedRows = ref([])       // files seleccionades (objects sencers)
const meta = ref({ count: 0, updated: 0, update_errors: [] })

const ENDPOINT = '/api/admin/magatzems/reexpedicio'
const REQUEST_TIMEOUT = 60000

// Capçaleres taula principal
const headers = [
  { title: 'Campaña', value: 'nom', sortable: true },
  { title: 'Cliente', value: 'client', sortable: true },
  { title: 'Fecha inicio', value: 'data_inici', sortable: true },
  { title: 'Fecha fin', value: 'data_fi', sortable: true },
  { title: 'Líneas', value: 'numLinies', sortable: false },
  { title: 'Unidades totales', value: 'unitatsTotals', sortable: true },
  { title: 'Descripción', value: 'descripcio', sortable: false }
]

// Capçaleres taula de línies (fila expandida)
const lineHeaders = [
  { title: 'Proveedor', value: 'proveidor' },
  { title: 'Ref. folleto', value: 'referencia' },
  { title: 'Cantidad', value: 'quantitat' }
]

// Helpers de format
const formatNumber = (n) => new Intl.NumberFormat('es-ES').format(Number(n || 0))

const truncate = (text, maxLen = 80) => {
  if (!text) return ''
  if (text.length <= maxLen) return text
  return text.slice(0, maxLen) + '…'
}

const formatDateHuman = (isoStr) => {
  if (!isoStr) return '—'
  // Accepta "YYYY-MM-DD" o ISO llarg
  const d = new Date(isoStr)
  if (Number.isNaN(d.getTime())) return isoStr
  return d.toLocaleDateString('es-ES', { year: 'numeric', month: '2-digit', day: '2-digit' })
}

// Transformació de dades des de l’API
const transformData = (payload) => {
  const arr = Array.isArray(payload?.data) ? payload.data : []

  const rows = arr.map((c) => {
    const linies = Array.isArray(c.linies) ? c.linies : []

    const unitatsTotals = linies.reduce(
      (acc, l) => acc + Number(l?.quantitat || 0),
      0
    )

    return {
      id: c.id,
      nom: String(c.nom || ''),
      descripcio: String(c.descripcio || ''),
      client: c.client || null,
      data_inici: c.data_inici || null,
      data_fi: c.data_fi || null,
      linies,
      numLinies: linies.length,
      unitatsTotals
    }
  })

  // Ordenem per data_inici asc i nom com a secundari
  rows.sort((a, b) => {
    const aDate = a.data_inici || ''
    const bDate = b.data_inici || ''
    if (aDate === bDate) {
      return a.nom.localeCompare(b.nom, 'es')
    }
    return aDate < bDate ? -1 : 1
  })

  return {
    rows,
    meta: payload?.meta || { count: rows.length, updated: 0, update_errors: [] }
  }
}

// Carregar campanyes
const fetchCampaigns = async () => {
  isLoading.value = true
  expanded.value = []
  selectedRows.value = []
  try {
    const { data } = await axios.get(ENDPOINT, {
      headers: { Accept: 'application/json' },
      timeout: REQUEST_TIMEOUT
    })

    const { rows, meta: m } = transformData(data)
    campaigns.value = rows
    meta.value = m

    if (rows.length === 0) {
      showAlertMessage('No hay campañas pendientes de rexpedición.', 'info')
    }
  } catch (e) {
    console.error('Error cargando campañas de rexpedición:', e)
    const message = e?.code === 'ECONNABORTED'
      ? 'La consulta está tardando demasiado. Vuelve a intentarlo.'
      : 'Error cargando campañas de rexpedición (Laravel/Odoo).'
    showAlertMessage(message, 'error')
  } finally {
    isLoading.value = false
  }
}

// Guardar rexpedides (POST idC[])
const saveRexpedidas = async () => {
  const selected = Array.isArray(selectedRows.value) ? selectedRows.value : []
  const ids = selected.map((row) => row.id).filter((id) => !!id)

  if (ids.length === 0) {
    showAlertMessage('Selecciona al menos una campaña para marcar como rexpedida.', 'warning')
    return
  }

  isSaving.value = true
  try {
    const { data } = await axios.post(
      ENDPOINT,
      { idC: ids },
      {
        headers: { Accept: 'application/json' },
        timeout: REQUEST_TIMEOUT
      }
    )

    const { rows, meta: m } = transformData(data)
    campaigns.value = rows
    meta.value = m
    selectedRows.value = []

    if (m.updated > 0) {
      showAlertMessage(
        `Se han marcado ${m.updated} campaña(s) como rexpedidas.`,
        'success'
      )
    } else {
      showAlertMessage('No se ha actualizado ninguna campaña.', 'info')
    }

    if (Array.isArray(m.update_errors) && m.update_errors.length > 0) {
      console.warn('Errores actualizando campañas en Odoo:', m.update_errors)
      showAlertMessage(
        'Algunas campañas no se han podido actualizar. Revisa los logs.',
        'warning'
      )
    }
  } catch (e) {
    console.error('Error guardando campañas rexpedidas:', e)
    showAlertMessage('Error guardando campañas rexpedidas (Laravel/Odoo).', 'error')
  } finally {
    isSaving.value = false
  }
}

onMounted(fetchCampaigns)
</script>
